// src/pages/PaymentBill.jsx

import React, { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { styled } from '@mui/material/styles';
import {
  Card,
  CardContent,
  CardActions,
  TextField,
  Stack,
  Typography,
  Button,
  Divider,
  Grid,
  Box,
  InputAdornment,
  Dialog,
  DialogContent,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import Iconify from '../components/iconify';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Services from 'src/services/Services';
import useResponsive from '../hooks/useResponsive';
import epLogo from '../assets/images/ep_logo.png';
import mlLogo from '../assets/images/ml_logo.png';

// ----------------------------------------------------------------------

const StyledRoot = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'flex',
    height: '100vh',
    overflow: 'hidden',
  },
}));

const StyledSection = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: 380,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  boxShadow: theme.customShadows?.card,
  backgroundColor: theme.palette.background.default,
}));

const StyledContent = styled('div')(({ theme }) => ({
  flex: 1,
  height: '100%',
  overflowY: 'auto',
  padding: theme.spacing(12, 2),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(6, 2),
  },
}));

const AMEX_BINS = ['375557', '375627', '375628', '375629', '375625'];

function detectCardBrand(cardNumber) {
  const number = cardNumber.replace(/\s/g, '');
  if (/^4/.test(number)) return 'visa';
  if (/^(5[1-5]|222[1-9]|22[3-9]\d|2[3-6]\d\d|27[01]\d|2720)/.test(number))
    return 'mastercard';
  if (/^3[47]/.test(number)) return 'amex';
  if (/^(6011|65|64[4-9]|622)/.test(number)) return 'discover';
  if (/^(30[0-5]|36|38|39)/.test(number)) return 'diners';
  if (/^35(2[89]|[3-8][0-9])/.test(number)) return 'jcb';
  return 'unknown';
}

function formatCardNumber(value) {
  const digits = value.replace(/\D/g, '');
  const groups = digits.match(/.{1,4}/g) || [];
  return groups.join(' ');
}

function formatExpiry(value) {
  let raw = value.replace(/\D/g, '').slice(0, 6);
  if (raw.length > 2) {
    return raw.slice(0, 2) + ' / ' + raw.slice(2);
  }
  return raw;
}

export default function PaymentBill() {
  const navigate = useNavigate();
  const location = useLocation();
  const { invoice, tax, item } = location.state || {};
  const mdUp = useResponsive('up', 'md');

  const [nameOnCard, setNameOnCard] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [phone, setPhone] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [open3d, setOpen3d] = useState(false);
  const [loading, setLoading] = useState(false);
  const pollingRef = useRef(null);

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

  const truncate2 = (num) => Math.floor((num || 0) * 100) / 100;

  // 2) Toplamları yeniden hesapla (truncate uygulanmış)
  const totalService =
    item?.lineItems?.reduce(
      (sum, li) => sum + truncate2((li.tutar || 0) - (li.vergi || 0)),
      0
    ) || 0;

  const totalVat =
    item?.lineItems?.reduce((sum, li) => sum + truncate2(li.vergi || 0), 0) ||
    0;

  const totalWithVat = truncate2(item?.price || 0);

  // 3) displayData bloğu
  const displayData = {
    statusText: item?.isPaid ? 'Ödenmiş' : 'Ödenmemiş',
    invoiceNumber: item?.invoiceID,
    invoiceNo: item?.invoiceID,
    taxNo: item?.taxNo,
    invoiceDate: item?.invoiceDate ? formatDate(item.invoiceDate) : '-',
    paymentDate: item?.paymentDate ? formatDate(item.paymentDate) : '-',
    fromName: item?.from.companyTitle,
    fromAddress: item?.from.address,
    fromPhone: item?.from.phone || '-',
    toName: item?.to.companyTitle,
    toAddress: item?.to.address,
    toPhone: item?.to.phone || '-',

    // Line item’lar
    items:
      item?.lineItems?.map((li, idx) => {
        const tutar = li.tutar || 0;
        const vergi = li.vergi || 0;
        return {
          id: idx + 1,
          description: li.stokKod,
          detail: li.kaynak || '',
          mall: item?.to.mallTitle,
          quantity: li.miktar,

          // Net fiyat = (tutar - vergi) truncate edilmiş
          unitPrice: truncate2(tutar - vergi).toLocaleString('tr-TR', {
            style: 'currency',
            currency: item.currency || 'TRY',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),

          vatRate: `${((vergi / (tutar || 1)) * 100).toFixed(0)}%`,

          // Vergi tutarı truncate edilmiş
          vatAmount: truncate2(vergi).toLocaleString('tr-TR', {
            style: 'currency',
            currency: item.currency || 'TRY',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),

          // Brüt toplam = tutar truncate edilmiş
          total: truncate2(tutar).toLocaleString('tr-TR', {
            style: 'currency',
            currency: item.currency || 'TRY',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
        };
      }) || [],

    // Özet kutuları (truncate edilmiş toplam değerler)
    totalAmount: truncate2(totalService).toLocaleString('tr-TR', {
      style: 'currency',
      currency: item.currency || 'TRY',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
    vatCalculated: truncate2(totalVat).toLocaleString('tr-TR', {
      style: 'currency',
      currency: item.currency || 'TRY',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
    totalWithVat: truncate2(totalWithVat).toLocaleString('tr-TR', {
      style: 'currency',
      currency: item.currency || 'TRY',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
    payableAmount: truncate2(totalWithVat).toLocaleString('tr-TR', {
      style: 'currency',
      currency: item.currency || 'TRY',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),

    notes: [
      `${truncate2(totalWithVat).toLocaleString('tr-TR', {
        style: 'currency',
        currency: item.currency || 'TRY',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} dir.`,
      'Vade : 30 Gün',
      'Vadesinde ödenmeyen faturalar için hizmet durdurma ve aylık %3,5 gecikme faizi uygulanır.',
    ],
  };

  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  const listenPayment = async (conversationID) => {
    try {
      const res = await Services.ListenPaymentML(conversationID);
      const status = res?.data?.result;
      if (status === '1' || status === '2') {
        clearInterval(pollingRef.current);
        setOpen3d(false);
        if (status === '1') {
          try {
            await Services.SetPaid(invoice, tax);
            toast.success('Ödeme başarılı.');
            navigate('/success-payment-bill', {
              state: { invoice, tax },
            });
          } catch (err) {
            console.error(err);
            toast.error(
              'Ödeme başarılı ancak fatura güncellenemedi. Lütfen tekrar deneyin.'
            );
          }
        } else {
          toast.error('Ödeme başarısız.');
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const raw = cardNumber.replace(/\s+/g, '');
      const is15 = raw.length === 15;
      const prefix2 = raw.slice(0, 2);
      const prefix6 = raw.slice(0, 6);
      if (
        is15 &&
        (prefix2 === '34' || prefix2 === '37' || AMEX_BINS.includes(prefix6))
      ) {
        toast.error('Amex kart geçerli değildir.');
        return;
      }
      const [mm, yyyy] = expiry.split('/').map((s) => s.trim());
      const expMonth = mm;
      const expYear = yyyy.slice(-2);

      const body = {
        invoiceID: invoice,
        taxNo: tax,
        creditCardInfos: {
          holderName: nameOnCard,
          cardNumber,
          expMonth,
          expYear,
          cvc: cvv,
          phone,
        },
      };

      const res = await Services.CreatePaymentRequest(body);
      const { conversationID, htmlContent } = res.data.result;
      setHtmlContent(htmlContent);
      setOpen3d(true);

      pollingRef.current = setInterval(
        () => listenPayment(conversationID),
        2000
      );
    } catch {
      toast.error('Ödeme isteği oluşturulamadı.');
    } finally {
      setLoading(false);
    }
  };

  const brand = detectCardBrand(cardNumber);
  let brandIcon = null;
  if (brand === 'visa')
    brandIcon = <Iconify icon="logos:visa" width={24} height={24} />;
  if (brand === 'mastercard')
    brandIcon = <Iconify icon="logos:mastercard" width={24} height={24} />;
  if (brand === 'amex')
    brandIcon = (
      <Iconify icon="logos:american-express" width={24} height={24} />
    );
  if (brand === 'discover')
    brandIcon = <Iconify icon="logos:discover" width={24} height={24} />;
  if (brand === 'diners')
    brandIcon = <Iconify icon="logos:diners-club" width={24} height={24} />;
  if (brand === 'jcb')
    brandIcon = <Iconify icon="logos:jcb" width={24} height={24} />;

  return (
    <>
      <Helmet>
        <title>Kart Bilgileri | Mall Logistics Panel</title>
      </Helmet>

      <StyledRoot>
        {mdUp && (
          <StyledSection>
            <Box
              sx={{
                backgroundColor: '#103996',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <img src="/assets/logo.png" alt="Logo" />
            </Box>
            <Typography variant="h3" sx={{ px: 5, mt: 5, mb: 5 }}>
              Fatura Ödeme
            </Typography>
            <img
              src="/assets/illustrations/login-ill.png"
              alt="login-ill"
              style={{ width: '100%' }}
            />
          </StyledSection>
        )}

        <StyledContent>
          <Box sx={{ width: '100%', maxWidth: 800, margin: 'auto' }}>
            {!item ? (
              <Typography sx={{ textAlign: 'center' }}>
                Fatura verisi bulunamadı.
              </Typography>
            ) : (
              <Card>
                <CardContent>
                  {/* Başlık & Durum */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 2 }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Iconify
                        icon="lets-icons:check-fill"
                        color={item.isPaid ? 'green' : 'gray'}
                        width={22}
                        height={22}
                      />
                      <Typography variant="h6">
                        Fatura Ödeme{' '}
                        <span
                          style={{
                            fontSize: 14,
                            fontWeight: 500,
                            color:
                              displayData.statusText === 'Ödenmemiş'
                                ? '#c4251a'
                                : '#C3C3C3',
                          }}
                        >
                          ({displayData.statusText})
                        </span>
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ color: '#979797' }}>
                      {displayData.invoiceNumber}
                    </Typography>
                  </Stack>

                  <Divider sx={{ mb: 2 }} />

                  {/* Mevcut Fatura Detayları */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 2,
                    }}
                  >
                    <img src={mlLogo} alt="ML Logo" style={{ height: 120 }} />
                    <img src={epLogo} alt="EP Logo" style={{ height: 100 }} />
                  </Box>

                  {/* İlk Grid: Fatura No ve Vergi No mobilde alt alta */}
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                      <Stack direction="column" spacing={1} sx={{ mb: 2 }}>
                        <Typography
                          variant="body2"
                          sx={{ color: 'text.secondary' }}
                        >
                          Fatura No
                        </Typography>
                        <Typography variant="subtitle2">
                          {displayData.invoiceNo}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: 'text.secondary' }}
                        >
                          Vergi No
                        </Typography>
                        <Typography variant="subtitle2">
                          {displayData.taxNo}
                        </Typography>
                      </Stack>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ color: 'text.secondary', fontWeight: 'bold' }}
                        >
                          Kimden
                        </Typography>
                        <Typography variant="subtitle2">
                          {displayData.fromName}
                        </Typography>
                        <Typography variant="body2">
                          {displayData.fromAddress}
                        </Typography>
                        <Typography variant="body2">
                          {displayData.fromPhone}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Stack spacing={2}>
                        <Box>
                          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{ color: 'text.secondary' }}
                            >
                              Fatura Tarihi
                            </Typography>
                            <Typography variant="subtitle2">
                              {displayData.invoiceDate}
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{ color: 'text.secondary' }}
                            >
                              Ödeme Tarihi
                            </Typography>
                            <Typography variant="subtitle2">
                              {displayData.paymentDate}
                            </Typography>
                          </Stack>
                        </Box>
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{ color: 'text.secondary', fontWeight: 'bold' }}
                          >
                            Kime
                          </Typography>
                          <Typography variant="subtitle2">
                            SAYIN {displayData.toName}
                          </Typography>
                          <Typography variant="body2">
                            {displayData.toAddress}
                          </Typography>
                          <Typography variant="body2">
                            {displayData.toPhone}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  </Grid>

                  {/* Tablo */}
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Sn</TableCell>
                          <TableCell>Mal / Hizmet</TableCell>
                          <TableCell>Açıklama</TableCell>
                          <TableCell>AVM Adı</TableCell>
                          <TableCell align="right">Miktar</TableCell>
                          <TableCell align="right">Birim Fiyat</TableCell>
                          <TableCell align="right">KDV %</TableCell>
                          <TableCell align="right">KDV Tutarı</TableCell>
                          <TableCell align="right">Toplam</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {displayData.items.map((row) => (
                          <TableRow key={row.id}>
                            <TableCell>{row.id}</TableCell>
                            <TableCell>{row.description}</TableCell>
                            <TableCell>{row.detail}</TableCell>
                            <TableCell>{row.mall}</TableCell>
                            <TableCell align="right">{row.quantity}</TableCell>
                            <TableCell align="right">{row.unitPrice}</TableCell>
                            <TableCell align="right">20%</TableCell>
                            <TableCell align="right">{row.vatAmount}</TableCell>
                            <TableCell align="right">{row.total}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* İkinci Grid: Özet kutuları mobilde alt alta */}
                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    {/* Soldaki özet kutusu */}
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          border: '1px solid rgba(0,0,0,0.3)',
                          borderRadius: '10px',
                          p: 1,
                          maxWidth: 360,
                          bgcolor: 'background.paper',
                        }}
                      >
                        <Grid container spacing={1}>
                          <Grid item xs={12} md={6}>
                            <Stack spacing={0.5}>
                              <Typography variant="caption" fontWeight="bold">
                                Versiyon : {item?.version || '-'}
                              </Typography>
                              <Typography variant="caption" fontWeight="bold">
                                Senaryo : {item?.scenario || '-'}
                              </Typography>
                              <Typography variant="caption" fontWeight="bold">
                                Fatura Tipi : {item?.invoiceType || '-'}
                              </Typography>
                            </Stack>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Stack spacing={0.5}>
                              <Typography variant="caption" fontWeight="bold">
                                Fatura No : {displayData.invoiceNo}
                              </Typography>
                              <Typography variant="caption" fontWeight="bold">
                                Fatura Tarihi : {displayData.invoiceDate}
                              </Typography>
                              <Typography variant="caption" fontWeight="bold">
                                Fatura Vadesi :{' '}
                                {item?.dueDate ? formatDate(item.dueDate) : '-'}
                              </Typography>
                              <Typography variant="caption" fontWeight="bold">
                                Conversation ID : {item?.conversationID || '-'}
                              </Typography>
                            </Stack>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>

                    {/* Sağdaki alt toplamlar kutusu */}
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          border: '1px solid rgba(0,0,0,0.2)',
                          p: 2,
                          borderRadius: '5px',
                        }}
                      >
                        <Stack spacing={1}>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2">
                              Mal Hizmet Toplam Tutarı
                            </Typography>
                            <Typography variant="body2">
                              {displayData.totalAmount}
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2">
                              Hesaplanan KDV
                            </Typography>
                            <Typography variant="body2">
                              {displayData.vatCalculated}
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2">
                              Vergiler Dahil Toplam Tutar
                            </Typography>
                            <Typography variant="body2">
                              {displayData.totalWithVat}
                            </Typography>
                          </Stack>
                          <Divider />
                          <Stack direction="row" justifyContent="space-between">
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 'bold' }}
                            >
                              Ödenecek Tutar
                            </Typography>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 'bold' }}
                            >
                              {displayData?.payableAmount}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Açıklamalar */}
                  <Box
                    sx={{
                      mt: 2,
                      pt: 2,
                      borderTop: '1px solid rgba(0,0,0,0.2)',
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 'bold' }}
                    >
                      AÇIKLAMALAR
                    </Typography>
                    {displayData.notes.map((note, i) => (
                      <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                        {note}
                      </Typography>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>

          {/* Ödeme Formu Kartı */}
          <Card
            sx={{
              maxWidth: 800,
              width: '100%',
              margin: 'auto',
              marginTop: '25px',
            }}
          >
            <CardContent>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 3 }}
                spacing={1}
              >
                <Typography variant="h5">Fatura Ödeme</Typography>
                <Typography variant="body2" color="text.secondary">
                  Fatura No: #{invoice}
                </Typography>
              </Stack>

              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label="Kart üzerindeki isim"
                  placeholder="Kart üzerindeki isim"
                  value={nameOnCard}
                  onChange={(e) => setNameOnCard(e.target.value)}
                />

                <TextField
                  fullWidth
                  label="Kart numarası"
                  placeholder="1234 5678 1234 4444"
                  value={formatCardNumber(cardNumber)}
                  onChange={(e) =>
                    setCardNumber(
                      e.target.value.replace(/\D/g, '').slice(0, 16)
                    )
                  }
                  InputProps={{
                    startAdornment: brandIcon && (
                      <InputAdornment position="start">
                        {brandIcon}
                      </InputAdornment>
                    ),
                  }}
                />

                <Grid container alignItems="center">
                  <Grid item xs={6} sm={4} style={{ marginRight: 15 }}>
                    <TextField
                      fullWidth
                      label="Son Kul. Tar."
                      placeholder="MM / YYYY"
                      value={expiry}
                      onChange={(e) => setExpiry(formatExpiry(e.target.value))}
                    />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <TextField
                      fullWidth
                      label="CVV"
                      placeholder="123"
                      inputProps={{ maxLength: 3 }}
                      value={cvv}
                      onChange={(e) =>
                        setCvv(e.target.value.replace(/\D/g, '').slice(0, 4))
                      }
                    />
                  </Grid>
                </Grid>

                <TextField
                  fullWidth
                  label="Telefon"
                  placeholder="5XXXXXXXX"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                />
              </Stack>
            </CardContent>

            <Divider />

            <CardActions sx={{ p: 2, flexDirection: 'column', gap: '15px' }}>
              <Button
                onClick={handleSubmit}
                variant="contained"
                color="primary"
                fullWidth
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Ödemeyi Tamamla'
                )}
              </Button>
              <Button
                variant="contained"
                color="secondary"
                fullWidth
                onClick={() => navigate('/pay-bill')}
                style={{ marginLeft: 0 }}
              >
                Yeni Bir Fatura Öde
              </Button>
            </CardActions>
          </Card>
        </StyledContent>
      </StyledRoot>

      <Dialog open={open3d} maxWidth="md" fullWidth>
        <DialogContent sx={{ p: 0 }}>
          <iframe
            title="3D Ödeme"
            srcDoc={htmlContent}
            style={{ width: '100%', height: '500px', border: 0 }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
